import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { QUOTE_DOCUMENT_TEMPLATE_NAME_MAX_LENGTH } from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useQuoteDocumentTemplatesStore } from "./quoteDocumentTemplatesStore";

interface ImportQuoteTemplateSheetProps {
  onClose: () => void;
  onCreated: (templateId: string) => void;
}

/** Creates a reusable template from pasted HTML through standard document validation. */
export function ImportQuoteTemplateSheet({
  onClose,
  onCreated,
}: ImportQuoteTemplateSheetProps) {
  const createTemplate = useQuoteDocumentTemplatesStore(
    (state) => state.createTemplate,
  );
  const [name, setName] = useState("");
  const [document, setDocument] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const save = async () => {
    if (!name.trim() || !document.trim()) {
      setError("Enter a template name and paste the template HTML.");
      return;
    }
    setError(null);
    setIsSaving(true);
    const result = await createTemplate({ name: name.trim(), document });
    setIsSaving(false);
    if (!result.template) {
      setError(result.error);
      return;
    }
    toast.success(`"${result.template.name}" created`);
    onClose();
    onCreated(result.template.id);
  };

  return (
    <Sheet open onOpenChange={(open) => !open && !isSaving && onClose()}>
      <SheetContent className="flex w-full flex-col sm:w-[640px] sm:max-w-[640px]">
        <SheetHeader>
          <SheetTitle>Paste template</SheetTitle>
          <SheetDescription>
            Paste a copied quote template and give it a name.
          </SheetDescription>
        </SheetHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
          <div className="space-y-2">
            <Label htmlFor="import-template-name">Template name</Label>
            <Input
              id="import-template-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={QUOTE_DOCUMENT_TEMPLATE_NAME_MAX_LENGTH}
              placeholder="e.g. Standard proposal"
              autoFocus
              disabled={isSaving}
            />
          </div>
          <div className="flex min-h-[220px] flex-1 flex-col gap-2">
            <Label htmlFor="import-template-html">Template source</Label>
            <Textarea
              id="import-template-html"
              value={document}
              onChange={(event) => setDocument(event.target.value)}
              placeholder="Paste your copied template here"
              spellCheck={false}
              className="min-h-[200px] flex-1 resize-none font-mono text-xs"
              disabled={isSaving}
            />
          </div>
          {error && (
            <p
              role="alert"
              className="whitespace-pre-wrap break-words text-sm text-destructive"
            >
              {error}
            </p>
          )}
        </div>
        <SheetFooter className="flex-row justify-end gap-2 sm:space-x-0">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={isSaving}>
            Create template
            {isSaving && <Loader2 className="size-4 animate-spin" />}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
